import { ShoppingBagIcon } from "@heroicons/react/24/solid";
import { GraphQLError } from "graphql";
import { Text, DropdownMenu, Button, Card } from "north.js";
import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { useQueryParam } from "use-query-params";

import { HardwareRequestStatus } from "src/api/types.generated";
import {
  Divider,
  Header,
  PageWrapper,
  Spinner,
  Icon,
  Modal,
  errorToast,
  successToast,
} from "src/shared/components";
import { Search } from "src/shared/components/Hardware";
import { EVENT_ID } from "src/shared/constants/event";
import { useEventContext } from "src/shared/contexts";
import { ChevronDownIcon } from "src/static/icons";

import { useAddItemToHardwareRequestMutation } from "../../graphql/addItemToHardwareRequest.generated";
import { useCreateHardwareRequestMutation } from "../../graphql/createHardwareRequest.generated";
import { useGetHackerHardwareRequestsQuery } from "../../graphql/getHackerHardwareRequests.generated";
import { useGetMyHardwareRequestItemsQuery } from "../../graphql/getMyHardwareRequestItems.generated";
import { useRemoveItemFromHardwareRequestMutation } from "../../graphql/removeItemFromHardwareRequest.generated";
import { useSubmitHardwareRequestMutation } from "../../graphql/submitHardwareRequest.generated";
import { useUpdateHardwareItemRequestQuantityMutation } from "../../graphql/updateHardwareItemRequestQuantity.generated";
import { useGetHardwareInfo } from "../../shared/useGetHardwareInfo";
import { getRandomHardwareLoadingMessage } from "../shared/loadingTextUtil";

import LotteryEntryModal from "./LotteryEntryModal";

const HackerInventory: React.FC = () => {
  const { hardware, isLoading: isEventLoading } = useEventContext();
  const takingRequests = hardware.takingRequests;
  const [query, setQuery] = useQueryParam<string>("search");
  const [filterParam] = useQueryParam<string>("filterParam");
  const [selectedCategory, setSelectedCategory] = useState<number>(
    filterParam ? parseInt(filterParam) : -1
  );

  const {
    hardwareCategories,
    hardwareSkus,
    inventoryItems,
    hardwareCategoriesDataLoading,
    hardwareSkusDataLoading,
    hardwareItemsLoading,
  } = useGetHardwareInfo({ includeItems: true });

  const [addItemToHardwareRequest] = useAddItemToHardwareRequestMutation();
  const [createHardwareRequest] = useCreateHardwareRequestMutation();
  const [removeItemFromHardwareRequest] =
    useRemoveItemFromHardwareRequestMutation();
  const [submitHardwareRequest, { loading: submitting }] =
    useSubmitHardwareRequestMutation();
  const [updateHardwareItemRequestQuantity] =
    useUpdateHardwareItemRequestQuantityMutation();
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [cartModalOpen, setCartModalOpen] = useState(false);
  const [descriptionModalOpen, setDescriptionModalOpen] = useState(false);
  const [selectedItemDescription, setSelectedItemDescription] =
    useState<string>("");
  const [selectedItemName, setSelectedItemName] = useState<string>("");
  const [lotteryEntryModalOpen, setLotteryEntryModalOpen] = useState(false);
  const [selectedLotterySku, setSelectedLotterySku] = useState<string>("");

  // Get the user's active hardware request (WAITING status)
  const { data: hardwareRequestsData, refetch: refetchHardwareRequests } =
    useGetHackerHardwareRequestsQuery({
      variables: {
        where: {
          status: { equals: "WAITING" as HardwareRequestStatus },
          event_id: { equals: EVENT_ID },
        },
      },
    });

  const activeRequest = hardwareRequestsData?.myHardwareRequests?.[0];
  const requestId = activeRequest?.id;

  // Get items in the active request
  const { data: requestItemsData, refetch: refetchRequestItems } =
    useGetMyHardwareRequestItemsQuery({
      variables: {
        hardware_request_id: requestId || 0,
      },
      skip: !requestId,
    });

  const onSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.currentTarget.value === "") {
        setQuery("");
      } else {
        setQuery(e.currentTarget.value);
      }
    },
    [setQuery]
  );

  const handleQuantityChange =
    (itemId: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = Number(e.target.value);
      setQuantities((prev) => ({ ...prev, [itemId]: value }));
    };

  const handleInfoClick = useCallback((name: string, description: string) => {
    setSelectedItemName(name);
    setSelectedItemDescription(description);
    setDescriptionModalOpen(true);
  }, []);

  const handleQuantityIncrease = useCallback(
    async (itemRequestId: number, currentQuantity: number) => {
      if (!takingRequests) return;
      try {
        await updateHardwareItemRequestQuantity({
          variables: {
            id: itemRequestId,
            quantity: currentQuantity + 1,
          },
        });
        refetchRequestItems();
      } catch (e) {
        errorToast((e as GraphQLError)?.message);
      }
    },
    [takingRequests, updateHardwareItemRequestQuantity, refetchRequestItems]
  );

  const handleQuantityDecrease = useCallback(
    async (itemRequestId: number, currentQuantity: number) => {
      if (currentQuantity <= 1) return;
      try {
        await updateHardwareItemRequestQuantity({
          variables: {
            id: itemRequestId,
            quantity: currentQuantity - 1,
          },
        });
        refetchRequestItems();
      } catch (e) {
        errorToast((e as GraphQLError)?.message);
      }
    },
    [updateHardwareItemRequestQuantity, refetchRequestItems]
  );

  const handleRemoveItem = useCallback(
    async (itemRequestId: number) => {
      try {
        await removeItemFromHardwareRequest({
          variables: {
            id: itemRequestId,
          },
        });
        refetchRequestItems();
      } catch (e) {
        errorToast((e as GraphQLError)?.message);
      }
    },
    [removeItemFromHardwareRequest, refetchRequestItems]
  );

  const handleAdd = async (itemId: number) => {
    if (!takingRequests) return;
    try {
      const hardwareItem = inventoryItems.find((item) => item.id === itemId);
      if (!hardwareItem) return;

      const quantity = Number(quantities[itemId] ?? 1);
      if (!quantity || quantity <= 0) return;

      let id = requestId;
      if (!id) {
        const { data } = await createHardwareRequest({
          variables: { event_id: EVENT_ID },
        });
        id = data?.createHardwareRequest?.id || undefined;
        if (!id) return;

        // Refetch hardware requests to get the new request ID
        await refetchHardwareRequests();
      }

      await addItemToHardwareRequest({
        variables: {
          hardware_request_id: id,
          hardware_item_id: hardwareItem.id,
          quantity,
        },
      });

      // Refetch the request items to update the cart
      if (id) {
        refetchRequestItems();
        successToast(
          `Added ${quantity} ${hardwareItem.sku.name || "item"} to cart`
        );
      }
    } catch (e) {
      errorToast((e as GraphQLError)?.message);
    }
  };

  const handleLotteryEntry = (skuCode: string) => {
    if (!takingRequests) return;
    setLotteryEntryModalOpen(true);
    setSelectedLotterySku(skuCode);
  };

  const handleSubmit = async () => {
    if (!takingRequests || !requestId) return;
    try {
      await submitHardwareRequest({ variables: { id: requestId } });
      // After submitting, close the cart and refetch to update the state
      setCartModalOpen(false);
      await refetchHardwareRequests();
      await refetchRequestItems();
      successToast("Hardware request submitted successfully");
    } catch (e) {
      errorToast(
        (e as GraphQLError)?.message || "Failed to submit hardware request"
      );
    }
  };

  const filteredHardwareItems = useMemo(() => {
    let filtered = [...inventoryItems];

    if (selectedCategory !== -1) {
      filtered = filtered.filter(
        (item) => item.sku.category_id === selectedCategory
      );
    }

    if (query && query.trim() !== "") {
      const lowercaseQuery = query.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.sku.name.toLowerCase().includes(lowercaseQuery) ||
          item.sku.description.toLowerCase().includes(lowercaseQuery)
      );
    }

    // Sort alphabetically by name
    return filtered.sort((a, b) => a.sku.name.localeCompare(b.sku.name));
  }, [inventoryItems, selectedCategory, query]);

  const loadingMessage = useMemo(() => {
    return getRandomHardwareLoadingMessage();
  }, []);

  const totalCartItems = useMemo(() => {
    return (
      requestItemsData?.myHardwareRequestItems?.reduce(
        (sum, item) => sum + item.quantity,
        0
      ) ?? 0
    );
  }, [requestItemsData]);

  if (
    hardwareCategoriesDataLoading ||
    hardwareSkusDataLoading ||
    hardwareItemsLoading ||
    isEventLoading
  ) {
    return <Spinner text={loadingMessage} />;
  }

  return (
    <>
      <Header
        title="Hardware"
        subtitle={
          takingRequests
            ? "Browse and request a variety of hardware to bring your project to life!"
            : "Browse our hardware, but note that requests are currently closed!"
        }
        actionButton={
          <CartButton onClick={() => setCartModalOpen(true)} color="primary">
            <CartIcon />
            {totalCartItems > 0 && <CartBadge>{totalCartItems}</CartBadge>}
          </CartButton>
        }
      />
      <PageWrapper>
        <PageContainer>
          <InventoryHeader>
            <Text type="h2">Inventory</Text>
            <EmptyStateText type="body1">
              {takingRequests
                ? "View inventory and request items."
                : "View inventory, but note that requests are currently closed!"}
            </EmptyStateText>
          </InventoryHeader>
          <FlexRow>
            <SearchWrapper>
              <Search
                query={query || ""}
                onSearchChange={onSearchChange}
                totalSearchResults={filteredHardwareItems.length}
                placeholder="Search all items"
              />
            </SearchWrapper>
            <DropdownMenu
              items={[
                [
                  {
                    content: <Text type="body1">All</Text>,
                    action: () => setSelectedCategory(-1),
                    align: "left" as const, // need const for typecheck
                  },
                ],
                hardwareCategories?.map((category) => ({
                  content: <Text type="body1">{category.name}</Text>,
                  action: () => setSelectedCategory(category.id),
                  align: "left",
                })) || [],
              ]}
              style={{ justifyContent: "start" }}
            >
              <Button size="lg">
                {hardwareCategories.find(
                  (category) => category.id === selectedCategory
                )?.name || "All"}
                <ChevronDownIcon />
              </Button>
            </DropdownMenu>
          </FlexRow>
        </PageContainer>
        {filteredHardwareItems.length === 0 ? (
          <EmptyStateText type="body1">
            No items match your search.
          </EmptyStateText>
        ) : (
          <ItemCardsWrapper>
            {filteredHardwareItems.map((item) => {
              const isSpecialItem = item.sku.type === "LOTTERY";
              const hasDescription = Boolean(item.sku.description);
              const showSideBar = hasDescription || isSpecialItem;

              return (
                <ItemCard key={item.id}>
                  {showSideBar && (
                    <ItemSideBar>
                      {hasDescription && (
                        <InfoIcon
                          name="info"
                          onClick={() =>
                            handleInfoClick(item.sku.name, item.sku.description)
                          }
                        />
                      )}
                      {isSpecialItem && (
                        <SpecialItemStar title="Special Item">
                          <span role="img" aria-label="Special Item">
                            ⭐
                          </span>
                        </SpecialItemStar>
                      )}
                    </ItemSideBar>
                  )}
                  <ItemImage src={item.sku.image} alt={item.sku.name} />
                  <ItemDetails>
                    <Text type="body1">{item.sku.name}</Text>
                    <EmptyStateText type="body2">
                      {item.available_quantity} available
                    </EmptyStateText>
                  </ItemDetails>
                  <ItemActions>
                    {!takingRequests && item.sku.type !== "FREE" ? (
                      <EmptyStateText type="body2">
                        Requests closed
                      </EmptyStateText>
                    ) : item.sku.type === "LOTTERY" ? (
                      <Button
                        color="primary"
                        onClick={() => handleLotteryEntry(item.sku.code)}
                        style={{ flex: 1 }}
                      >
                        Request Special Item
                      </Button>
                    ) : item.sku.type === "FREE" ? (
                      <Text type="body1">Available at QNX Makerspace</Text>
                    ) : (
                      <QuantityControls>
                        <Button
                          onClick={() => {
                            const currentQty = quantities[item.id] ?? 1;
                            if (currentQty > 1) {
                              handleQuantityChange(item.id)({
                                target: { value: currentQty - 1 },
                              } as any);
                            }
                          }}
                        >
                          <Text type="body1">-</Text>
                        </Button>

                        <QuantityDisplay type="body1">
                          {quantities[item.id] ?? 1}
                        </QuantityDisplay>

                        <Button
                          onClick={() => {
                            const currentQty = quantities[item.id] ?? 1;
                            if (currentQty < item.available_quantity) {
                              handleQuantityChange(item.id)({
                                target: { value: currentQty + 1 },
                              } as any);
                            }
                          }}
                        >
                          <Text type="body1">+</Text>
                        </Button>

                        <Button
                          onClick={() => handleAdd(item.id)}
                          color="primary"
                          style={{ marginLeft: "8px" }}
                        >
                          <Icon
                            name="shopping-cart"
                            size="20px"
                            color="brandPrimary"
                          />
                        </Button>
                      </QuantityControls>
                    )}
                  </ItemActions>
                </ItemCard>
              );
            })}
          </ItemCardsWrapper>
        )}
      </PageWrapper>

      <Modal
        isOpen={cartModalOpen}
        onClose={() => setCartModalOpen(false)}
        title="Hardware Request Cart"
        width="600px"
      >
        <CartContent>
          {!requestItemsData?.myHardwareRequestItems?.length ? (
            <CartEmptyText type="body1">Your cart is empty</CartEmptyText>
          ) : (
            <>
              {requestItemsData.myHardwareRequestItems.map((item) => {
                const skuInfo = hardwareSkus.find(
                  (sku) => sku.code === item.HardwareItem.sku_code
                );
                return (
                  <CartItem key={item.id}>
                    <CartItemImage
                      src={skuInfo?.image || ""}
                      alt={skuInfo?.name || ""}
                    />
                    <CartItemDetails>
                      <CartItemName type="body1">
                        {skuInfo?.name || "Unknown Item"}
                      </CartItemName>
                    </CartItemDetails>
                    <CartItemControls>
                      <Button
                        size="md"
                        color="secondary"
                        onClick={() =>
                          handleQuantityDecrease(item.id, item.quantity)
                        }
                        disabled={item.quantity <= 1}
                      >
                        -
                      </Button>
                      <CartItemQuantity type="body1">
                        {item.quantity}
                      </CartItemQuantity>
                      <Button
                        size="md"
                        color="secondary"
                        onClick={() =>
                          handleQuantityIncrease(item.id, item.quantity)
                        }
                        disabled={!takingRequests}
                      >
                        +
                      </Button>
                      <Button
                        size="md"
                        color="secondary-danger"
                        onClick={() => handleRemoveItem(item.id)}
                        style={{ marginLeft: "8px" }}
                      >
                        Remove
                      </Button>
                    </CartItemControls>
                  </CartItem>
                );
              })}
              <Divider />
              <CartFooter>
                <Text type="h3">Total Items: {totalCartItems}</Text>
                <Button
                  color="primary"
                  size="lg"
                  disabled={!takingRequests || !totalCartItems || submitting}
                  onClick={handleSubmit}
                >
                  {takingRequests ? "Submit Request" : "Requests Closed"}
                </Button>
              </CartFooter>
            </>
          )}
        </CartContent>
      </Modal>

      <Modal
        isOpen={descriptionModalOpen}
        onClose={() => setDescriptionModalOpen(false)}
        title={selectedItemName || "Item Description"}
        width="500px"
      >
        <ModalContent>
          <Text type="body1">
            {selectedItemDescription || "No description available."}
          </Text>
        </ModalContent>
      </Modal>

      <LotteryEntryModal
        isOpen={lotteryEntryModalOpen}
        onClose={() => setLotteryEntryModalOpen(false)}
        skuCode={selectedLotterySku}
        hardwareSkus={hardwareSkus}
      />
    </>
  );
};

const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const ItemCardsWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 4px;
  max-width: 100%;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 4px;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 4px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 4px;
  }
`;

const SearchWrapper = styled.div`
  width: 100%;
`;

const CartButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.globalConstants.borderRadius.small}px;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${({ theme }) =>
      theme.globalConstants.color.backgroundGray};
  }
`;

const CartBadge = styled.div`
  position: absolute;
  top: -4px;
  right: -4px;
  background-color: ${({ theme }) => theme.globalConstants.color.brandPrimary};
  color: ${({ theme }) => theme.globalConstants.color.white};
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.globalConstants.fontSize.caption}px;
  font-weight: 600;
`;

const CartContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 0;
`;

const CartItem = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: ${({ theme }) => theme.globalConstants.borderRadius.small}px;
`;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.globalConstants.padding.medium};
  margin-bottom: ${({ theme }) => theme.globalConstants.padding.medium};
`;

const InventoryHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ItemCard = styled(Card)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 8px;
  height: 100%;
  border-radius: ${({ theme }) => theme.globalConstants.borderRadius.small}px;
`;

const ItemSideBar = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ItemImage = styled.img`
  height: 150px;
  margin: 4px;
  object-fit: contain;
  border-radius: ${({ theme }) => theme.globalConstants.borderRadius.small}px;
`;

const ItemDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  text-align: center;
`;

const ItemActions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: auto;
  width: 100%;
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const QuantityDisplay = styled(Text)`
  min-width: 30px;
  text-align: center;
  font-weight: bold;
`;

const CartIcon = styled(ShoppingBagIcon)`
  width: 32px;
  height: 32px;
  color: ${({ theme }) => theme.globalConstants.color.greenPrimary};
`;

const InfoIcon = styled(Icon)`
  cursor: pointer;
  min-width: 24px;
  min-height: 24px;
  flex-shrink: 0;
  padding: 4px;
`;

const SpecialItemStar = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  min-height: 24px;
  padding: 4px;
  font-size: 16px;
  line-height: 1;
  cursor: default;
  user-select: none;
`;

const EmptyStateText = styled(Text)`
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
`;

const CartEmptyText = styled(Text)`
  text-align: center;
  padding: 40px;
`;

const CartItemImage = styled.img`
  width: 50px;
  height: 50px;
`;

const CartItemDetails = styled.div`
  flex: 1;
`;

const CartItemName = styled(Text)`
  font-weight: 600;
`;

const CartItemControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CartItemQuantity = styled(Text)`
  min-width: 20px;
  text-align: center;
`;

const CartFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalContent = styled.div`
  padding: 16px 0;
`;

export default HackerInventory;
